import type { Vector3, Vector3Tuple } from 'three';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { AP_ID, CLIENT_ID } from 'modules/devices/constants';
import { DEFAULT_AP_POSITION, DEFAULT_CLIENT_POSITION } from 'modules/editor/constants';

export type Device = {
  position: Vector3Tuple;
};

interface Position {
  x: number;
  y: number;
}

export type State = {
  state: 'dragging' | 'panning' | 'idle';

  // coefficient for maintaining static object size no matter camera's zoom level
  scaleFactor: number;

  showTooltip: boolean;
  hoverDeviceId: string | null;
  hoverLinkId: string | null;
  tooltipPosition: Position | null;
  pointerPosition: Position | null;

  devices: Record<string, Device>;
  setDevicePosition: (id: string, position: Device['position']) => void;
  addDevice: (id: string, device: Device) => void;
  deleteDevice: (id: string) => void;

  contextMenu: {
    open: boolean;
    position: Vector3 | null;
    id: string | null;
  };
  updateContextMenu: (updates: Partial<State['contextMenu']>) => void;
};

export const useEditorStore = create<State>()(
  subscribeWithSelector(
    immer((set) => ({
      state: 'idle',
      scaleFactor: 1,
      showTooltip: false,
      hoverDeviceId: null,
      hoverLinkId: null,
      tooltipPosition: null,
      pointerPosition: null,
      devices: {
        [AP_ID]: {
          position: [DEFAULT_AP_POSITION.x, DEFAULT_AP_POSITION.y, 0],
        },
        [CLIENT_ID]: {
          position: [DEFAULT_CLIENT_POSITION.x, DEFAULT_CLIENT_POSITION.y, 0],
        },
      },
      addDevice: (id, device) =>
        set((state) => {
          state.devices[id] = device;
        }),
      deleteDevice: (id) =>
        set((state) => {
          delete state.devices[id];
        }),
      setDevicePosition: (id, position) =>
        set((state) => {
          state.devices[id].position = position;
        }),

      contextMenu: {
        open: false,
        position: null,
        id: null,
      },
      updateContextMenu: (updates) => set((prev) => ({ contextMenu: { ...prev.contextMenu, ...updates } })),
    }))
  )
);

if (import.meta.env.DEV) {
  (window as Window & { __wifiCalculatorEditorStore?: typeof useEditorStore }).__wifiCalculatorEditorStore =
    useEditorStore;
}
